import React, { useState, useMemo } from 'react';
import { Search, Plus, Minus, X, Package, PackagePlus, ToggleLeft, ToggleRight } from 'lucide-react';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Button } from '../ui/button';
import { Dialog, DialogContent } from '../ui/dialog';
import { batchStockUpdate } from '../../services/api';
import { toast } from 'sonner';

export const BatchStockModal = ({ open, onOpenChange, products, onRefresh }) => {
  const [batchList, setBatchList] = useState([]);
  const [batchSearch, setBatchSearch] = useState('');

  const batchSearchResults = useMemo(() => {
    if (!batchSearch.trim()) return [];
    const q = batchSearch.toLowerCase();
    return products.filter(p =>
      (p.name.toLowerCase().includes(q) || (p.barcode && p.barcode.toLowerCase().includes(q))) &&
      !batchList.find(b => b.product_id === p.id)
    ).slice(0, 8);
  }, [products, batchSearch, batchList]);

  const handleBatchBarcodeEnter = (e) => {
    if (e.key !== 'Enter' || !batchSearch.trim()) return;
    const exact = products.find(p => p.barcode && p.barcode.toLowerCase() === batchSearch.toLowerCase().trim());
    if (exact && !batchList.find(b => b.product_id === exact.id)) {
      addToBatchList(exact);
      setBatchSearch('');
    }
  };

  const addToBatchList = (product) => {
    setBatchList(prev => [...prev, {
      product_id: product.id, name: product.name, barcode: product.barcode,
      current_stock: product.current_stock, cost_price: product.cost_price || '',
      sale_price: product.default_price || '', expiry_date: product.expiry_date || '',
      amount: 1, update_price: false, update_expiry: false,
    }]);
    setBatchSearch('');
  };

  const updateBatchItem = (idx, field, value) => {
    setBatchList(prev => prev.map((item, i) => i === idx ? { ...item, [field]: value } : item));
  };

  const removeBatchItem = (idx) => {
    setBatchList(prev => prev.filter((_, i) => i !== idx));
  };

  const handleBatchSubmit = async () => {
    if (batchList.length === 0) { toast.error('Listeye en az 1 ürün ekleyin'); return; }
    try {
      const items = batchList.map(item => ({
        product_id: item.product_id, amount: parseInt(item.amount) || 0,
        update_price: item.update_price,
        cost_price: item.update_price ? (parseFloat(item.cost_price) || 0) : null,
        sale_price: item.update_price ? (parseFloat(item.sale_price) || 0) : null,
        update_expiry: item.update_expiry,
        expiry_date: item.update_expiry ? item.expiry_date : null,
      }));
      const res = await batchStockUpdate(items);
      toast.success(res.data.message);
      onOpenChange(false);
      setBatchList([]);
      onRefresh();
    } catch (e) { toast.error(e.response?.data?.detail || 'Hata oluştu'); }
  };

  const handleClose = (isOpen) => {
    if (!isOpen) { setBatchList([]); setBatchSearch(''); }
    onOpenChange(isOpen);
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent data-testid="batch-stock-modal" className="sm:rounded-2xl sm:max-w-md lg:max-w-3xl sm:max-h-[75vh] overflow-hidden flex flex-col p-0">
        <div className="px-4 lg:px-6 pt-4 pb-3 flex items-center justify-between border-b border-gray-100 shrink-0">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 bg-green-50 rounded-xl flex items-center justify-center shrink-0">
              <PackagePlus size={18} className="text-green-600" />
            </div>
            <div>
              <h2 className="text-base lg:text-lg font-bold text-gray-900">{`Hızlı Stok Girişi`}</h2>
              <span className="text-xs text-[#8E8E93]">{batchList.length} {`ürün listede`}</span>
            </div>
          </div>
        </div>

        <div className="flex-1 overflow-y-auto">
          <div className="px-4 lg:px-6 py-3 border-b border-gray-100 sticky top-0 bg-white z-10">
            <div className="relative">
              <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#8E8E93]" />
              <Input value={batchSearch} onChange={e => setBatchSearch(e.target.value)} onKeyDown={handleBatchBarcodeEnter}
                placeholder={`Ürün adı veya barkod ile ara...`} className="h-10 bg-[#F2F2F7] border-0 rounded-xl pl-9" autoFocus data-testid="batch-stock-search" />
            </div>
            {batchSearch.trim() && batchSearchResults.length > 0 && (
              <div className="mt-2 bg-white border border-gray-200 rounded-xl overflow-hidden shadow-sm max-h-40 overflow-y-auto">
                {batchSearchResults.map(p => (
                  <button key={p.id} onClick={() => addToBatchList(p)}
                    className="w-full text-left px-3 py-2 hover:bg-gray-50 border-b border-gray-100 last:border-0 flex items-center justify-between" data-testid={`batch-search-${p.id}`}>
                    <div>
                      <p className="text-sm font-medium text-gray-900">{p.name}</p>
                      {p.barcode && <p className="text-[10px] text-[#8E8E93]">{p.barcode}</p>}
                    </div>
                    <span className="text-xs font-semibold text-[#6b6de3]">{p.current_stock} adet</span>
                  </button>
                ))}
              </div>
            )}
            {batchSearch.trim() && batchSearchResults.length === 0 && (
              <p className="text-xs text-[#8E8E93] mt-2 text-center">{`Ürün bulunamadı`}</p>
            )}
          </div>

          {batchList.length === 0 ? (
            <div className="text-center py-10">
              <Package size={36} className="text-[#C7C7CC] mx-auto mb-2" />
              <p className="text-sm text-[#8E8E93]">{`Ürün arayarak listeye ekleyin`}</p>
            </div>
          ) : (
            <div className="divide-y divide-gray-200">
              {batchList.map((item, idx) => (
                <div key={item.product_id} className="px-4 lg:px-6 py-3 space-y-2" data-testid={`batch-item-${idx}`}>
                  <div className="flex items-center gap-2">
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-semibold text-gray-900 truncate">{item.name}</p>
                      <p className="text-[11px] text-[#8E8E93]">{`Mevcut: `}<span className="font-bold text-[#6b6de3]">{item.current_stock}</span></p>
                    </div>
                    <div className="flex items-center bg-[#F2F2F7] rounded-lg shrink-0">
                      <button onClick={() => updateBatchItem(idx, 'amount', Math.max(0, (parseInt(item.amount) || 0) - 1))} className="w-8 h-8 flex items-center justify-center text-[#8E8E93]"><Minus size={14} /></button>
                      <Input type="number" value={item.amount} onChange={e => updateBatchItem(idx, 'amount', e.target.value)}
                        className="w-12 h-8 text-center text-sm font-bold bg-transparent border-0 p-0" data-testid={`batch-amount-${idx}`} />
                      <button onClick={() => updateBatchItem(idx, 'amount', (parseInt(item.amount) || 0) + 1)} className="w-8 h-8 flex items-center justify-center text-[#6b6de3]"><Plus size={14} /></button>
                    </div>
                    <button onClick={() => removeBatchItem(idx)} className="p-1 text-[#C7C7CC] hover:text-red-500"><X size={16} /></button>
                  </div>
                  <div className="flex items-center gap-4">
                    <button onClick={() => updateBatchItem(idx, 'update_price', !item.update_price)}
                      className={`flex items-center gap-1.5 text-xs font-medium ${item.update_price ? 'text-[#6b6de3]' : 'text-[#8E8E93]'}`} data-testid={`batch-price-toggle-${idx}`}>
                      {item.update_price ? <ToggleRight size={18} /> : <ToggleLeft size={18} />} {`Fiyat Güncelle`}
                    </button>
                    <button onClick={() => updateBatchItem(idx, 'update_expiry', !item.update_expiry)}
                      className={`flex items-center gap-1.5 text-xs font-medium ${item.update_expiry ? 'text-[#6b6de3]' : 'text-[#8E8E93]'}`} data-testid={`batch-expiry-toggle-${idx}`}>
                      {item.update_expiry ? <ToggleRight size={18} /> : <ToggleLeft size={18} />} {`SKT Güncelle`}
                    </button>
                  </div>
                  {item.update_price && (
                    <div className="flex gap-2">
                      <div className="flex-1">
                        <Label className="text-[10px] text-[#8E8E93]">{`Alış Fiyatı`}</Label>
                        <Input type="number" value={item.cost_price} onChange={e => updateBatchItem(idx, 'cost_price', e.target.value)}
                          placeholder="0" className="h-8 text-sm bg-[#F2F2F7] border-0 rounded-lg" data-testid={`batch-cost-${idx}`} />
                      </div>
                      <div className="flex-1">
                        <Label className="text-[10px] text-[#8E8E93]">{`Satış Fiyatı`}</Label>
                        <Input type="number" value={item.sale_price} onChange={e => updateBatchItem(idx, 'sale_price', e.target.value)}
                          placeholder="0" className="h-8 text-sm bg-[#F2F2F7] border-0 rounded-lg" data-testid={`batch-sale-${idx}`} />
                      </div>
                    </div>
                  )}
                  {item.update_expiry && (
                    <div>
                      <Label className="text-[10px] text-[#8E8E93]">{`Son Kullanma Tarihi`}</Label>
                      <Input type="date" value={item.expiry_date} onChange={e => updateBatchItem(idx, 'expiry_date', e.target.value)}
                        className="h-8 text-sm bg-[#F2F2F7] border-0 rounded-lg w-full" data-testid={`batch-expiry-${idx}`} />
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>

        {batchList.length > 0 && (
          <div className="p-4 lg:p-6 border-t border-gray-100 shrink-0">
            <Button onClick={handleBatchSubmit} className="w-full bg-[#34C759] hover:bg-[#2DA84E] rounded-full h-11 font-bold" data-testid="batch-stock-submit">
              <PackagePlus size={16} className="mr-2" /> {batchList.length} {`Ürünü Güncelle`}
            </Button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};
